/**
 * THE PHASE 1 CONTRACT.
 *
 * This file is the single coordination point for the microsite work: the block
 * union, the theme, and the page/document shapes. The database jsonb columns,
 * the Astro renderer, the provisioning service and (later) the agent tools all
 * key off these types. Change them deliberately — a silent shape change here
 * breaks three packages at once, and `blocks: jsonb` means TypeScript will not
 * tell you.
 *
 * Zod schemas live in ./schemas.ts and MUST stay in exact correspondence with
 * these types (`satisfies` where possible). Types alone are not validation —
 * jsonb round-trips whatever it is given.
 */
/**
 * Data-bound blocks hold a QUERY, never a copy of the data. This is the whole
 * differentiator: services, team, hours and prices stay live, so a price edit
 * in the dashboard shows on the website without a republish and without the
 * agent being involved. Never denormalise business data into `props`.
 */
export const DATA_BOUND_BLOCKS = [
    'services',
    'team',
    'opening_hours',
    'map_location',
];
/**
 * THE canonical variant list. First entry is the fallback: an unknown variant
 * renders as `[0]` rather than throwing, so one bad block never takes down a
 * tenant's whole page.
 *
 * This lives in the contract, not in the renderer and not in the feature
 * catalogue, because it was independently invented in BOTH and the two lists
 * disagreed — `services` was `cards|list|price-menu` in one and something else
 * in the other. A variant the renderer cannot draw is a blank section on a
 * live customer site, so there is exactly one list and both sides derive from
 * it.
 */
export const BLOCK_VARIANTS = {
    hero: ['image-right', 'full-bleed', 'text-only'],
    services: ['cards', 'list', 'price-menu'],
    team: ['grid', 'row'],
    gallery: ['grid', 'carousel'],
    opening_hours: ['table', 'inline'],
    map_location: ['split', 'map-full', 'address-only'],
    cta_booking: ['band', 'panel'],
    rich_text: ['prose', 'narrow'],
};
/** Unknown variant -> the block's fallback. Never throws. */
export function resolveVariant(type, variant) {
    const allowed = BLOCK_VARIANTS[type];
    return (allowed.includes(variant) ? variant : allowed[0]);
}
/**
 * The `name` attribute Meta looks for when it crawls a tenant's custom domain
 * to verify ownership (plan §9.4).
 *
 * It lives in the CONTRACT because both sides need it and neither can import
 * the other: the token is read from the database in `features/microsites`, and
 * the tag is emitted by the Astro renderer, which must never reach the
 * database. Duplicating the string is how the two quietly stop matching and
 * verification fails with nothing in the logs.
 */
export const META_DOMAIN_VERIFY_TAG_NAME = 'facebook-domain-verify';
//# sourceMappingURL=contract.js.map